import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import TicketService from "services/ticketService";

const initialState = {
  loading: false,
  tickets: [],
  selectedTicket: null,
  successMessage: "",
  errorMessage: "",
};

export const getTickets = createAsyncThunk(
  "tickets/getTickets",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await TicketService.getTickets(params);
      return response;
    } catch (err) {
      return rejectWithValue(err.message || "Error fetching tickets");
    }
  }
);

export const getTicket = createAsyncThunk(
  "tickets/getTicket",
  async (ticketId, { rejectWithValue }) => {
    try {
      const response = await TicketService.getTicket(ticketId);
      return response;
    } catch (err) {
      return rejectWithValue(err.message || "Error fetching ticket");
    }
  }
);

export const createTicket = createAsyncThunk(
  "tickets/createTicket",
  async (data, { rejectWithValue }) => {
    try {
      const response = await TicketService.createTicket(data);
      return response;
    } catch (err) {
      return rejectWithValue(err.message || "Error creating ticket");
    }
  }
);

export const updateTicket = createAsyncThunk(
  "tickets/updateTicket",
  async ({ ticketId, data }, { rejectWithValue }) => {
    try {
      const response = await TicketService.updateTicket(ticketId, data);
      return response;
    } catch (err) {
      return rejectWithValue(err.message || "Error updating ticket");
    }
  }
);

export const deleteTicket = createAsyncThunk(
  "tickets/deleteTicket",
  async (ticketId, { rejectWithValue, dispatch }) => {
    try {
      await TicketService.deleteTicket(ticketId);
      dispatch(getTickets());
      return { ticketId, message: "Ticket deleted successfully" };
    } catch (err) {
      return rejectWithValue(err.message || "Error deleting ticket");
    }
  }
);

export const replyToTicket = createAsyncThunk(
  "tickets/replyToTicket",
  async ({ ticketId, data }, { rejectWithValue, dispatch }) => {
    try {
      const response = await TicketService.replyToTicket(ticketId, data);
      dispatch(getTicket(ticketId));
      return response;
    } catch (err) {
      return rejectWithValue(err.message || "Error sending reply");
    }
  }
);

const ticketSlice = createSlice({
  name: "tickets",
  initialState,
  reducers: {
    clearTicketMessage: (state) => {
      state.successMessage = "";
      state.errorMessage = "";
    },
    clearSelectedTicket: (state) => {
      state.selectedTicket = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getTickets.pending, (state) => {
        state.loading = true;
      })
      .addCase(getTickets.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.tickets = payload;
      })
      .addCase(getTickets.rejected, (state, { payload }) => {
        state.loading = false;
        state.errorMessage = payload;
      })

      .addCase(getTicket.pending, (state) => {
        state.loading = true;
      })
      .addCase(getTicket.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.selectedTicket = payload;
      })
      .addCase(getTicket.rejected, (state, { payload }) => {
        state.loading = false;
        state.errorMessage = payload;
      })

      .addCase(createTicket.pending, (state) => {
        state.loading = true;
      })
      .addCase(createTicket.fulfilled, (state) => {
        state.loading = false;
        state.successMessage = "Ticket created successfully";
      })
      .addCase(createTicket.rejected, (state, { payload }) => {
        state.loading = false;
        state.errorMessage = payload;
      })

      .addCase(updateTicket.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateTicket.fulfilled, (state) => {
        state.loading = false;
        state.successMessage = "Ticket updated successfully";
      })
      .addCase(updateTicket.rejected, (state, { payload }) => {
        state.loading = false;
        state.errorMessage = payload;
      })

      .addCase(deleteTicket.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteTicket.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.successMessage = payload.message;
        state.tickets = state.tickets.filter(
          (t) => t.ticket_id !== payload.ticketId
        );
      })
      .addCase(deleteTicket.rejected, (state, { payload }) => {
        state.loading = false;
        state.errorMessage = payload;
      })

      .addCase(replyToTicket.pending, (state) => {
        state.loading = true;
      })
      .addCase(replyToTicket.fulfilled, (state) => {
        state.loading = false;
        state.successMessage = "Reply sent successfully";
      })
      .addCase(replyToTicket.rejected, (state, { payload }) => {
        state.loading = false;
        state.errorMessage = payload;
      });
  },
});

export const { clearTicketMessage, clearSelectedTicket } = ticketSlice.actions;
export default ticketSlice.reducer;
